'use client';

import { BoxCTA } from '@lichtblick/photon/atoms/box-cta/box-cta';
import { TextCTA } from '@lichtblick/photon/atoms/text-cta/text-cta';
import { Modal } from '@lichtblick/photon/organisms/modal/modal';
import { useTracker } from '@lichtblick/tracker';
import { type FC, type PropsWithChildren, useState } from 'react';
import { i18n } from '@/i18n';
import { createEcommerceItem } from '@/tracking/create-ecommerce-item';
import { useLoggedIn } from '@/tracking/use-logged-in';
import type { TariffsQuery } from '@/types/generated';

type TariffDetailsLayerProps = PropsWithChildren<{
  index: number;
  isComboCheckout: boolean;
  product: TariffsQuery['tariffs'][number];
  renderLinkAs?: 'link' | 'button';
}>;

export const TariffDetailsModal: FC<TariffDetailsLayerProps> = ({
  children,
  index,
  isComboCheckout,
  product,
  renderLinkAs = 'link',
}) => {
  const loggedIn = useLoggedIn();

  const [isOpen, setIsOpen] = useState(false);
  const { trackEcommerceEvent } = useTracker();

  const onClickHandler = () => {
    setIsOpen(true);

    // only track in in root view
    if (index > -1) {
      trackEcommerceEvent({
        event: 'Ecommerce - Item Detail View',
        event_name: 'view_item',
        view_item: createEcommerceItem([{ ...product, itemIndex: index }], loggedIn, isComboCheckout),
      });
    }
  };

  return (
    <>
      {renderLinkAs === 'link' ? (
        <TextCTA
          as="button"
          data-dd-action-name={`Open details: ${product.productCode}`}
          data-testid="button-modal-product"
          onClick={onClickHandler}
          slim
        >
          Tarifdetails anzeigen
        </TextCTA>
      ) : (
        <BoxCTA
          as="button"
          data-dd-action-name={`Open details: ${product.productCode}`}
          data-testid="button-modal-product"
          variant="neutral-outline"
          size="small"
          onClick={onClickHandler}
        >
          Tarifdetails anzeigen
        </BoxCTA>
      )}

      <Modal
        footerContent={
          <TextCTA as="button" data-testid="button-modal-close" onClick={() => setIsOpen(false)}>
            {i18n.modal.confirm}
          </TextCTA>
        }
        isOpen={isOpen}
        onPointerDownOutside={() => {
          setIsOpen(false);
        }}
        title={`${product.name} - Tarifdetails`}
      >
        {children}
      </Modal>
    </>
  );
};
